import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createDataPort } from './data/factory';
import { annotateSubmission } from './data/teacher';
import { ensureSemanticDatabase, syncSemanticConfig } from './data/semantic';
import { dayMode, greeting } from './lib/time';
import { loadSettings, saveSettings } from './lib/settings';
import { QuickCapture } from './modules/capture/QuickCapture';
import { SettingsSheet } from './modules/settings/SettingsSheet';
import { AccountMenu } from './modules/account/AccountMenu';
import { cacheAccount, deleteCloudAccount, loadCachedAccount, type GraphiteAccount } from './lib/account';
import { Timeline } from './modules/timeline/Timeline';
import type { AppSettings, TimelineItem } from './types';
import { beginOrResumeTrace, finishTrace } from './lib/trace';

const RETRY_DELAY_MS = 5 * 60_000;

export function App() {
  const [settings, setSettings] = useState(loadSettings);
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('connections');
  const [account, setAccount] = useState<GraphiteAccount | null>(loadCachedAccount);
  const [revealPending, setRevealPending] = useState(false);
  const [busy, setBusy] = useState(true);
  const [notice, setNotice] = useState('');
  const mode = dayMode();
  const data = useMemo(() => createDataPort(settings), [settings.userKey]);
  const syncRunning = useRef(false);
  const syncAgain = useRef(false);
  const nextSyncAt = useRef(0);
  const enrichmentRunning = useRef(false);
  const noticeTimer = useRef<number | null>(null);

  const showNotice = useCallback((message: string, duration = 5_000) => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    setNotice(message);
    if (message && duration > 0) noticeTimer.current = window.setTimeout(() => { setNotice(''); noticeTimer.current = null; }, duration);
  }, []);
  useEffect(() => () => { if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current); }, []);

  useEffect(() => { saveSettings(settings); }, [settings]);
  useEffect(() => {
    void ensureSemanticDatabase().then(() => syncSemanticConfig(settings.configEndpoint, settings.serviceToken)).catch(() => { /* Offline uses the last downloaded copy. */ });
  }, [settings.configEndpoint, settings.serviceToken]);
  const reloadTimeline = useCallback(async () => setItems(await data.listTimeline(settings.userKey)), [data, settings.userKey]);
  useEffect(() => { let active = true; setBusy(true); data.listTimeline(settings.userKey).then((next) => active && setItems(next)).catch((error) => active && showNotice(error.message)).finally(() => active && setBusy(false)); return () => { active = false; }; }, [data, settings.userKey, showNotice]);

  const applyAccount = useCallback((next: GraphiteAccount | null) => {
    setAccount(next); cacheAccount(next);
    setSettings((current) => ({ ...current, userKey: next?.identifier || 'default', userName: next?.name || 'Основной пользователь', userId: 1 }));
  }, []);

  const syncNow = useCallback(async (force = false) => {
    if (!account || !data.sync || !navigator.onLine) return;
    if (!force && Date.now() < nextSyncAt.current) return;
    if (syncRunning.current) { syncAgain.current = true; return; }
    syncRunning.current = true;
    try {
      const result = await data.sync(settings.cloudEndpoint, account.identifier);
      nextSyncAt.current = 0;
      await reloadTimeline();
      if (result.warnings?.length) showNotice(`Синхронизация сохранила основные данные, но пропустила ${result.warnings.length} несовместимых производных. Подробности доступны в Debug.`);
    } catch (error) {
      nextSyncAt.current = Date.now() + RETRY_DELAY_MS;
      if (navigator.onLine) showNotice('Облачная синхронизация временно отложена; локальные данные сохранены.');
    } finally {
      syncRunning.current = false;
      if (syncAgain.current) { syncAgain.current = false; void syncNow(); }
    }
  }, [account, data, reloadTimeline, settings.cloudEndpoint, showNotice]);

  useEffect(() => {
    if (!account) return;
    void syncNow(true);
    const online = () => { nextSyncAt.current = 0; void syncNow(true); };
    window.addEventListener('online', online);
    const timer = window.setInterval(() => void syncNow(), 30_000);
    return () => { window.removeEventListener('online', online); window.clearInterval(timer); };
  }, [account, syncNow]);

  const runEnrichmentQueue = useCallback(async () => {
    if (enrichmentRunning.current || !data.listDueEnrichments || !data.completeEnrichment || !data.retryEnrichment) return;
    enrichmentRunning.current = true;
    try {
      while (true) {
        const jobs = await data.listDueEnrichments(new Date().toISOString());
        if (!jobs.length) break;
        for (const job of jobs) {
          const traceId = beginOrResumeTrace(job.id, job.payload.label, settings.userKey);
          try {
            if (job.payload.entryId === null) throw new Error('Teacher-разметка требует текстовую запись');
            await annotateSubmission(data, job.payload.userId ? { ...settings, userId: job.payload.userId } : settings, job.payload.entryId, job.payload.entryText, showNotice, traceId, job.id);
            finishTrace(traceId);
            await data.completeEnrichment(job.id);
            void syncNow();
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Неизвестная ошибка анализа';
            await data.retryEnrichment(job.id, new Date(Date.now() + RETRY_DELAY_MS).toISOString(), message);
            showNotice('Текст сохранён. Teacher-разметка продолжится с незавершённой операции через 5 минут; причина ожидания — в Debug.');
          }
        }
      }
    } finally {
      enrichmentRunning.current = false;
    }
  }, [data, settings, showNotice, syncNow]);

  useEffect(() => {
    void runEnrichmentQueue();
    const online = () => void runEnrichmentQueue();
    window.addEventListener('online', online);
    const timer = window.setInterval(() => void runEnrichmentQueue(), 30_000);
    return () => { window.removeEventListener('online', online); window.clearInterval(timer); };
  }, [runEnrichmentQueue]);

  const submit = async (text: string) => {
    setBusy(true);
    try {
      const hasText = Boolean(text.trim());
      if (!hasText) return;
      const state = mode === 'evening' ? 'pending_reflection' as const : 'active' as const;
      const userId = await data.ensureUser(settings.userKey, settings.userName);
      if (userId !== settings.userId) setSettings((current) => ({ ...current, userId }));
      const item = await data.createEntry({ text, state, source: 'text', userId });
      setItems((current) => [item, ...current]);
      showNotice('Persona-текст сохранён; teacher-разметка идёт в фоне');
      const payload = {
        entryId: item.id,
        entryText: text,
        userId,
        label: 'Teacher-разметка persona-текста',
      };
      if (data.enqueueEnrichment) await data.enqueueEnrichment(payload);
      void syncNow();
      void runEnrichmentQueue();
    } catch (error) { showNotice(error instanceof Error ? error.message : 'Не удалось сохранить persona-текст'); }
    finally { setBusy(false); }
  };

  const complete = async (id: number) => { await data.updateState(id, 'completed'); setItems((current) => current.map((item) => item.id === id ? { ...item, state: 'completed' } : item)); void syncNow(); };
  const removeData = async () => {
    if (account) {
      if (!window.confirm('Удалить аккаунт и все его данные из облака? Локальная база останется на этом устройстве.')) return;
      await deleteCloudAccount(settings);
      await data.resetSync?.(account.identifier);
      applyAccount(null);
      showNotice('Аккаунт и облачные данные удалены. Локальная база сохранена.');
    } else {
      if (!window.confirm('Удалить все локальные данные с этого устройства? Это действие нельзя отменить.')) return;
      await data.clearAll?.();
      await reloadTimeline();
      showNotice('Локальные данные удалены');
    }
  };

  return <div className={`app-shell mode-${mode}`}>
    <div className="grain" />
    <header className="quiet-header"><div><span>Эта неделя</span><h1>{greeting(mode)}</h1></div><AccountMenu settings={settings} account={account} onSettings={(tab) => { setSettingsTab(tab); setSettingsOpen(true); }} onAccount={applyAccount} onNotice={showNotice} /></header>
    {notice && <div className="notice">{notice}</div>}
    <Timeline items={items} revealPending={revealPending} onTogglePending={() => setRevealPending((value) => !value)} onComplete={(id) => void complete(id)} />
    <QuickCapture evening={mode === 'evening'} busy={busy} onSubmit={submit} />
    {settingsOpen && <SettingsSheet value={settings} account={account} initialTab={settingsTab} onAccount={applyAccount} onChange={setSettings} onClose={() => setSettingsOpen(false)} onDeleteData={() => void removeData().catch((error) => showNotice(error instanceof Error ? error.message : 'Не удалось удалить данные'))} />}
  </div>;
}
